/**
 * SaveSnapshotDialog — create a user-initiated named snapshot of the
 * live network state.
 *
 * Why a dialog (instead of an inline text field on the toolbar):
 *
 *   * Backend dedupes on content-hash; if the state hasn't changed since
 *     the last save we need to surface the "already saved" outcome on a
 *     surface that won't get confused with a generic toast.
 *   * Creators sometimes type labels in-the-moment ("pre vendor demo"),
 *     so we want a focused input with visible char budget.
 *   * Cap / TTL is a first-class UX constraint, so the dialog owns the
 *     copy about "kept for 90 days, up to 20 per network".
 *
 * All wire-level work lives in ``networkRestoreApi.saveUserSnapshot``;
 * this component is a thin presenter + a focus-trapped modal shell.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveUserSnapshot } from '@/services/networkRestoreApi';
import { WORKBENCH_OUTLINE_BTN_MODAL } from '@/utils/workbenchOutlineButtonClassNames.js';

const LABEL_MAX = 60;

export default function SaveSnapshotDialog({
  open,
  networkId,
  onClose,
  onSaved,
}) {
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Inline confirmation when the backend returns ``deduped: true`` —
  // the network is byte-for-byte identical to the latest user snapshot
  // (sha256 over canonical-JSON, see ``_compute_snapshot_content_hash``).
  // We keep the dialog open with a clear "no new row was created"
  // message so the user knows their click was acknowledged AND the
  // history list didn't actually change.
  const [dedupedInfo, setDedupedInfo] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setError(null);
    setSubmitting(false);
    setDedupedInfo(null);
    // Defer focus a tick so framer-motion's enter animation doesn't
    // swallow the focus ring on slower machines.
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setDedupedInfo(null);
    try {
      const trimmed = label.trim();
      const response = await saveUserSnapshot(networkId, {
        label: trimmed.length ? trimmed : null,
      });
      if (response?.deduped) {
        // Surface the no-op outcome inline; do NOT call ``onSaved`` —
        // the parent uses that callback to close the dialog, and we
        // want the user to read this message before the modal goes
        // away. They dismiss with Done / Esc / backdrop click.
        const existingLabel = response?.snapshot?.label;
        setDedupedInfo(
          existingLabel
            ? `No changes since the last save — the existing snapshot "${existingLabel}" was reused.`
            : 'No changes since the last save — the existing snapshot was reused.',
        );
        return;
      }
      onSaved?.(response);
    } catch (e) {
      setError(e?.message || 'Failed to save snapshot');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (dedupedInfo) {
        onClose?.();
      } else {
        submit();
      }
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="save-snapshot-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="text-sm font-semibold text-gray-900">Snapshot</h3>
              <p className="mt-1 text-xs text-gray-600">
                Capture the current network state. You can restore from it
                later — it does not release the network.
              </p>
            </div>

            <div className="space-y-3 px-5 py-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">
                  Label <span className="text-gray-400">(optional)</span>
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value.slice(0, LABEL_MAX))}
                  onKeyDown={onKeyDown}
                  placeholder="e.g. pre vendor demo"
                  maxLength={LABEL_MAX}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <span className="mt-1 block text-[11px] text-gray-400">
                  {label.length}/{LABEL_MAX}
                </span>
              </label>

              <p className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
                Snapshots are kept for 90 days. Up to 20 per network — the
                oldest gets pruned automatically when the cap is reached.
                Saving an unchanged state is a no-op (deduped on a
                content hash so identical clicks won't pile up rows).
              </p>

              {dedupedInfo && (
                <p
                  className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                  role="status"
                  data-testid="save-snapshot-deduped"
                >
                  {dedupedInfo}
                </p>
              )}

              {error && (
                <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              {dedupedInfo ? (
                <button type="button" onClick={onClose} className={WORKBENCH_OUTLINE_BTN_MODAL}>
                  Done
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    className={WORKBENCH_OUTLINE_BTN_MODAL}
                  >
                    {submitting ? 'Saving…' : 'Snapshot'}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
